import React, { useState, useRef } from 'react'
import classnames from 'classnames'
import { useHistory } from 'react-router'
import Skeleton from 'react-loading-skeleton'

import { t } from 'src/translations'
import DropdownArrow from 'src/shared/components/common/dropdown/DropdownArrow'

import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { useIsMobile, ScreenWidth } from 'shared/js/utils/WindowContext'
import { useRestaurantRoutes } from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'

import { useGlobalNav } from 'public/components/default_template/GlobalNavContext'
import { ScheduleIcon } from 'public/components/default_template/menu_banner/ScheduleIcon'
import { getNavColorStyles } from 'public/components/default_template/nav/navUtils'
import { LocationSelector } from 'src/public/components/default_template/online_ordering/location_selection/LocationSelector'
import { useOOLocations } from 'public/components/online_ordering/FulfillmentContext'

import { LeftMenuPanel } from './LeftMenuPanel'
import { MenuGroup } from './types'
import { navigateToCategory } from './urlFilterUtils'
import { useCategoryFromSlug, useCategoryPath } from './useCategoryFromSlug'
import NavSearchWrapper from '../search/NavSearchWrapper'

export type MenuBannerProps = {
  menuGroups: MenuGroup[]
  setFilters: React.Dispatch<React.SetStateAction<Set<string>>>
  categorySlug?: string
  channelGuid?: string | null
  isEcomm?: boolean
  loading?: boolean
}

export const MenuBanner = ({
  menuGroups = [],
  setFilters,
  categorySlug,
  channelGuid,
  isEcomm = false,
  loading
}: MenuBannerProps) => {
  const { restaurant, isEcommMultiLocationEnabled } = useRestaurant()
  const { globalNavHeight, globalNavOpen } = useGlobalNav()
  const history = useHistory()
  const { ecommPath } = useRestaurantRoutes()
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<MenuGroup | null>(null)
  const [selectedChild, setSelectedChild] = useState<MenuGroup | null>(null)
  const searchContainerRef = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobile(ScreenWidth.SMALL)
  const ooLocations = useOOLocations()

  // Get the current category from slug to highlight it
  const activeCategory = useCategoryFromSlug(categorySlug, menuGroups)

  // Get the full category path for pre-selecting in left nav
  const categoryPath = useCategoryPath(categorySlug, menuGroups)

  // If there's only one menu group, use its children (sub-groups) for the banner
  const displayMenuGroups =
    menuGroups.length === 1 && menuGroups[0]?.children?.length
      ? menuGroups[0].children
      : menuGroups

  // Track if we're displaying sub-groups (children of a single parent)
  const isDisplayingSubGroups =
    menuGroups.length === 1 && menuGroups[0]?.children?.length
  const parentGroup = isDisplayingSubGroups ? menuGroups[0] : null

  // Get the same nav styling as the global navigation
  const navColorStyles = getNavColorStyles(restaurant, true)

  const handleGroupClick = (group: MenuGroup) => {
    const isActiveCategory = activeCategory?.guid === group.guid
    // If we're displaying sub-groups, the clicked group is actually a child
    if (isDisplayingSubGroups && parentGroup) {
      // If this child has no children, filter directly to it
      if (!group.children || group.children.length === 0) {
        if (group.slug && !isActiveCategory) {
          navigateToCategory(group, ecommPath, history, () => {})
        } else {
          setFilters(new Set([`${parentGroup.guid}:${group.guid}`]))
        }
        return // Don't open the panel
      }
      // If it has children, open the panel with parent selected and this child highlighted
      setSelectedGroup(parentGroup)
      setSelectedChild(group)
    } else {
      setSelectedGroup(group)
      setSelectedChild(null)
    }
    setIsPanelOpen(true)
  }

  const handleAllCategoriesClick = () => {
    // If we're on a category page, pre-select the parent groups to expand to the current category
    if (categoryPath) {
      // If the current category is a top-level group, select it
      // If it's nested, select the parent chain
      if (
        categoryPath.parent &&
        categoryPath.parent.guid !== categoryPath.category?.guid
      ) {
        setSelectedGroup(categoryPath.parent)
        // Only set selectedChild if the category is at level 3 (has both parent and child ancestors)
        if (
          categoryPath.child &&
          categoryPath.child.guid !== categoryPath.category?.guid
        ) {
          setSelectedChild(categoryPath.child)
        } else {
          setSelectedChild(null)
        }
      } else {
        setSelectedGroup(null)
        setSelectedChild(null)
      }
    } else {
      setSelectedGroup(null)
      setSelectedChild(null)
    }
    setIsPanelOpen(true)
  }

  const handlePanelClose = () => {
    setIsPanelOpen(false)
    setSelectedGroup(null)
    setSelectedChild(null)
  }

  const categoryIconSize = isMobile ? 24 : 16

  const shouldFormatDifferently =
    isEcommMultiLocationEnabled &&
    ooLocations?.length !== undefined &&
    !!ooLocations.length
  const shouldDisplayLocationPicker = !isMobile && shouldFormatDifferently

  return (
    <>
      <div
        className={classnames({ paddedContentWrapper: !isEcomm })}
        style={
          isEcomm
            ? ({
                ...navColorStyles,
                position: 'sticky',
                top: globalNavOpen ? `${globalNavHeight}px` : '0px',
                zIndex: 7
              } as React.CSSProperties)
            : undefined
        }
      >
        <div
          ref={searchContainerRef}
          className={classnames('menuBanner', {
            paddedContent: !isEcomm,
            isEcomm: isEcomm
          })}
          data-testid='menu-banner'
          style={
            {
              ...navColorStyles,
              ...(!isEcomm &&
                (globalNavOpen
                  ? { position: 'sticky', top: `${globalNavHeight}px` }
                  : { position: 'sticky', top: '0px' }))
            } as React.CSSProperties
          }
        >
          <div className='bannerContent'>
            <div className='menuGroupButtons' data-testid='menu-group-buttons'>
              {isMobile && (loading || displayMenuGroups.length === 1) && (
                <button
                  className='groupButton'
                  disabled={loading}
                  aria-disabled={loading || undefined}
                  aria-label={
                    loading ? t('menu-banner.loading-categories') : undefined
                  }
                  onClick={
                    loading
                      ? undefined
                      : () => handleGroupClick(displayMenuGroups[0]!)
                  }
                >
                  <ScheduleIcon
                    height={categoryIconSize}
                    width={categoryIconSize}
                  />
                </button>
              )}
              {!loading && displayMenuGroups.length > 1 && (
                <button
                  className='groupButton'
                  onClick={handleAllCategoriesClick}
                  data-testid='all-categories-button'
                >
                  <ScheduleIcon
                    height={categoryIconSize}
                    width={categoryIconSize}
                  />
                  {!isMobile && t('menu-banner.all-categories')}
                </button>
              )}
              {loading &&
                !isMobile &&
                Array.from({ length: 3 }).map((_, index) => (
                  <button
                    key={index}
                    className='groupButton'
                    disabled
                    aria-disabled='true'
                    data-testid='menu-banner-skeleton'
                  >
                    <Skeleton
                      width='120px'
                      containerClassName='loadingSkeleton'
                    />
                  </button>
                ))}
              {!loading &&
                !isMobile &&
                displayMenuGroups?.slice(0, 5).map((group) => {
                  const isActiveCategory = activeCategory?.guid === group.guid
                  return (
                    <button
                      key={group.guid}
                      className={classnames('groupButton', {
                        highlighted: isActiveCategory
                      })}
                      disabled={isActiveCategory}
                      onClick={() => handleGroupClick(group)}
                      data-testid={`group-button-${group.guid}`}
                    >
                      {group.name}
                      {group.children && group.children.length > 0 && (
                        <DropdownArrow />
                      )}
                    </button>
                  )
                })}
            </div>
            <div
              className={classnames('searchContainer', {
                withoutPicker: !shouldFormatDifferently
              })}
              data-testid='search-container'
            >
              <NavSearchWrapper channelGuid={channelGuid} />
              {!loading && shouldDisplayLocationPicker && <LocationSelector />}
            </div>
          </div>
        </div>
      </div>

      {!loading && (
        <LeftMenuPanel
          isOpen={isPanelOpen}
          menuGroups={menuGroups}
          selectedGroup={selectedGroup}
          initialSelectedChild={selectedChild}
          currentCategorySlug={categorySlug}
          onClose={handlePanelClose}
          onGroupSelect={setSelectedGroup}
          setFilters={setFilters}
        />
      )}
    </>
  )
}
